import axios from 'axios';
import { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/AuthContext";
import { MessageSquare, MessageSquarePlus, Send, Search, Users, Plus, Paperclip, X, Download, Image, FileText, ChevronLeft, Trash2, Check, CheckCheck, ZoomIn } from "lucide-react";
import { toast } from "sonner";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
// 🚀 [Phase v13.47 — 2026-07-18] Améliorations messagerie :
//   - Lightbox photos cliquables (zoom + pan + download)
//   - Compression image côté client avant upload
//   - Timestamps précis en tooltip + indicateur ✓/✓✓ style WhatsApp
import { MessagePhotoLightbox } from './Messagerie/MessagePhotoLightbox';
import { compressImage, formatTimestampFR, computeReadStatus } from './Messagerie/messageHelpers';
// 🚀 [Phase v13.47.1 — 2026-07-18] UX Messagerie enrichie :
//   - Textarea auto-resize (5 lignes max, Enter=send, Shift+Enter=nouvelle ligne)
//   - Linkify plain-text sécurisé (URLs / emails cliquables sans dangerouslySetInnerHTML)
//   - 2 boutons distincts : "Nouveau message" (1:1) vs "Nouveau groupe" (N)
import { ModalNouveauMessage } from './Messagerie/ModalNouveauMessage';
import { linkifyMessage } from '@/utils/linkifyMessage';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const MAX_FILE_MB = 5;

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `il y a ${hrs}h`;
  return `il y a ${Math.floor(hrs / 24)}j`;
}

function FilePreview({ fichier, isMe, onOpenLightbox }) {
  if (!fichier) return null;
  const isImage = fichier.type?.startsWith("image/");
  if (isImage) {
    // 🚀 [Phase v13.47 — 2026-07-18] Photo cliquable → ouvre lightbox zoom.
    // Wrap l'<img> dans un <button> pour accessibilité clavier + hover badge 🔍.
    return (
      <div className="mt-1.5 max-w-[240px]">
        <button
          type="button"
          onClick={() => onOpenLightbox?.(fichier)}
          className="relative group w-full block rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-400"
          data-testid="msg-photo-thumb-btn"
          title="Cliquer pour agrandir"
        >
          <img
            src={`data:${fichier.type};base64,${fichier.data_b64}`}
            alt={fichier.nom}
            className="w-full object-cover cursor-zoom-in group-hover:brightness-90 transition-all"
          />
          {/* Badge loupe au hover (indicateur visuel qu'on peut cliquer) */}
          <span
            className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-hidden="true"
          >
            <ZoomIn className="w-3 h-3" />
          </span>
        </button>
        <p className="text-[10px] mt-1 opacity-70">{fichier.nom}</p>
      </div>
    );
  }
  return (
    <a
      href={`data:${fichier.type};base64,${fichier.data_b64}`}
      download={fichier.nom}
      className={`mt-1.5 flex items-center gap-2 px-3 py-2 rounded-xl border text-xs ${isMe ? "border-white/30 bg-white/10 text-white hover:bg-white/20" : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"} transition-colors`}
    >
      <FileText className="w-4 h-4 flex-shrink-0" />
      <span className="truncate">{fichier.nom}</span>
      <Download className="w-3.5 h-3.5 ml-auto flex-shrink-0" />
    </a>
  );
}

// ─── 🚀 [Phase v13.47.1] Rendu texte multi-lignes + URLs/emails cliquables ─────
//   • Sauts de ligne : préservés via `white-space: pre-wrap` sur le <p> parent
//   • URLs / emails : découpés en tokens et rendus en <a target="_blank"> — pas
//     de dangerouslySetInnerHTML, safe by design.
function MessageTextRenderer({ texte, isMe }) {
  if (!texte) return null;
  const tokens = linkifyMessage(texte);
  const linkClass = isMe
    ? 'underline text-white/95 hover:text-white break-all'
    : 'underline text-blue-600 hover:text-blue-700 break-all';
  return (
    <p
      className="whitespace-pre-wrap break-words"
      data-testid="msg-text-body"
    >
      {tokens.map((tok, i) => {
        if (tok.type === 'text') {
          return <span key={i}>{tok.content}</span>;
        }
        return (
          <a
            key={i}
            href={tok.href}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            data-testid={`msg-link-${tok.type}`}
            onClick={(e) => e.stopPropagation()}
          >
            {tok.content}
          </a>
        );
      })}
    </p>
  );
}

// ─── Modal Nouveau Groupe ─────────────────────────────────────────────────────
function ModalNouveauGroupe({ employes, myId, myNom, onCreated, onClose }) {
  const [groupName, setGroupName] = useState("");
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);

  const toggle = (emp) => {
    setSelected(prev =>
      prev.find(x => x.id === emp.id) ? prev.filter(x => x.id !== emp.id) : [...prev, { id: emp.id, nom: `${emp.prenom} ${emp.nom}` }]
    );
  };

  const filtered = employes.filter(e => `${e.prenom} ${e.nom}`.toLowerCase().includes(search.toLowerCase()));

  const create = async () => {
    if (!groupName.trim() || selected.length < 1) {
      toast.error("Nom du groupe et au moins 1 membre requis");
      return;
    }
    setCreating(true);
    const r = await axios.post(`${API_URL}/api/messagerie/groupes`, { 
      group_name: groupName.trim()
    });
    if (r.status === 200) {
      const d = r.data;
      toast.success(`Groupe "${groupName}" créé !`);
      onCreated(d.conversation);
    } else {
      toast.error("Erreur création du groupe");
    }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Nouveau groupe</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label className="text-sm">Nom du groupe</Label>
            <Input
              data-testid="group-name-input"
              placeholder="Ex: Équipe soumissions, Direction..."
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              className="mt-1"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-sm">Membres ({selected.length} sélectionné{selected.length > 1 ? "s" : ""})</Label>
            <Input
              placeholder="Rechercher un employé..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="mt-1 mb-2"
            />
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {filtered.map(emp => {
                const isSelected = !!selected.find(x => x.id === emp.id);
                return (
                  <div
                    key={emp.id}
                    onClick={() => toggle(emp)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${isSelected ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50 border border-transparent"}`}
                  >
                    <Checkbox checked={isSelected} onCheckedChange={() => toggle(emp)} />
                    <EmployeeAvatar photoUrl={emp.photo_url} prenom={emp.prenom} nom={emp.nom} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{emp.prenom} {emp.nom}</p>
                      <p className="text-xs text-gray-400">{emp.role}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button
              data-testid="btn-create-group"
              onClick={create}
              disabled={creating || !groupName.trim() || selected.length < 1}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {creating ? "Création..." : "Créer le groupe"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Composant Principal ──────────────────────────────────────────────────────
export function Messagerie() {
  const { user } = useAuth();
  const [employes, setEmployes] = useState([]);
  const [employesLoaded, setEmployesLoaded] = useState(false); // ✅ OPTIMISATION: Lazy loading
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null); // {nom, type, data_b64}
  const [compressing, setCompressing] = useState(false);   // 🚀 v13.47 : loader compression
  const [lightboxFichier, setLightboxFichier] = useState(null); // 🚀 v13.47 : photo agrandie
  const [showDirectModal, setShowDirectModal] = useState(false); // 🚀 v13.47.1 : "Nouveau message"
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'chat'
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);   // 🚀 v13.47.1 : auto-resize du textarea

  // 🚀 [Phase v13.47.1 — 2026-07-18] Auto-resize du textarea.
  //   Recalcule la hauteur à chaque changement de `newMessage`. Max = 5 lignes
  //   (calculé à partir de line-height DOM réel). Après ça, scroll interne du
  //   textarea (overflow-y: auto).
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const paddingY =
      parseFloat(getComputedStyle(el).paddingTop) +
      parseFloat(getComputedStyle(el).paddingBottom);
    const maxHeight = lineHeight * 5 + paddingY;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  });

  const myId = user?.employee_id || user?.id || "";
  const myNom = user ? `${user.prenom} ${user.nom}` : "";

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  // ✅ OPTIMISATION: Chargement lazy des employés (seulement au besoin)
  const loadEmployees = useCallback(async () => {
    if (employesLoaded) return; // Ne charge qu'une fois
    try {
      // ✅ Charger TOUS les employés (actifs ET inactifs) pour afficher les photos correctement.
      // 🎨 [Phase v13.54 — 2026-07-22] Ajout `include_owners=true` pour inclure les
      // propriétaires stockés dans `db.users` (role=proprietaire) sans fiche
      // `db.employees`. Sans ce param, ces propriétaires étaient invisibles dans
      // le sélecteur de destinataires messagerie (ex : « Naskapidou », user
      // proprio de l'app). Voir /app/memory/rca_v13_54_messagerie_destinataires.md.
      const r = await axios.get(`${API_URL}/api/employees?include_owners=true`);
      setEmployes((r.data.employees || []).filter(e => e.id !== myId));
      setEmployesLoaded(true);
    } catch (e) {
      console.error("Erreur chargement employés:", e);
    }
  }, [myId, employesLoaded]);

  // ✅ OPTIMISATION: Limite à 20 conversations les plus récentes
  const loadConversations = useCallback(async () => {
    if (!myId) return;
    try {
      const r = await axios.get(`${API_URL}/api/messagerie/conversations?user_id=${myId}&limit=20`);
      if (r.status === 200) {
        const d = r.data;
        setConversations(d.conversations || []);
      }
    } catch (e) {
      console.error("Erreur chargement conversations:", e);
    }
  }, [myId]);

  // ✅ OPTIMISATION: Limite à 100 derniers messages par conversation
  const loadMessages = useCallback(async (convId) => {
    if (!convId || !myId) return;
    try {
      const r = await axios.get(`${API_URL}/api/messagerie/conversations/${convId}/messages?user_id=${myId}&limit=100`);
      if (r.status === 200) {
        const d = r.data;
        setMessages(d.messages || []);
        setTimeout(scrollToBottom, 50);
      }
    } catch (e) {
      console.error("Erreur chargement messages:", e);
    }
  }, [myId]);

  useEffect(() => { loadConversations(); loadEmployees(); }, [loadConversations, loadEmployees]);

  // ✅ OPTIMISATION: Polling réduit de 5s → 15s (moins de charge serveur)
  useEffect(() => {
    pollRef.current = setInterval(() => {
      loadConversations();
      if (selectedConv) loadMessages(selectedConv.id);
    }, 15000); // Changé de 5000 à 15000ms
    return () => clearInterval(pollRef.current);
  }, [loadConversations, loadMessages, selectedConv]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  const openDirect = async (employe) => {
    const r = await axios.post(`${API_URL}/api/messagerie/conversations`, { user1_id: myId, user1_nom: myNom, user2_id: employe.id, user2_nom: `${employe.prenom} ${employe.nom}` });
    if (r.status === 200) {
      const d = r.data;
      setSelectedConv(d.conversation);
      setMobileView('chat');
      await loadMessages(d.conversation.id);
      await loadConversations();
      setSearch("");
    }
  };

  const selectConv = async (conv) => {
    setSelectedConv(conv);
    setMobileView('chat');
    await loadMessages(conv.id);
    await loadConversations();
  };

  const handleFileAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (max ${MAX_FILE_MB} Mo)`);
      return;
    }
    // 🚀 [Phase v13.47 — 2026-07-18] Compression images côté client.
    //   Cible ~500 Ko max (1920×1080, JPEG q=0.75) pour :
    //     • Alléger MongoDB (base64 stocké en dur dans le message)
    //     • Réduire la charge réseau à chaque poll (15s)
    //     • Éviter le blocage à 5 Mo pour photos smartphones (souvent >5 Mo)
    //   Non-image (PDF, docs...) et formats spéciaux (SVG, GIF) passent tels quels.
    try {
      setCompressing(true);
      const result = await compressImage(file);
      setAttachedFile(result);
      if (result.taille_originale && result.taille_originale > result.taille) {
        const ratio = Math.round((1 - result.taille / result.taille_originale) * 100);
        toast.success(`Image compressée : ${(result.taille_originale / 1024).toFixed(0)} → ${(result.taille / 1024).toFixed(0)} Ko (-${ratio}%)`);
      }
    } catch (err) {
      console.error('Erreur compression fichier :', err);
      toast.error('Erreur lors du traitement du fichier');
    } finally {
      setCompressing(false);
      e.target.value = "";
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && !attachedFile) return;
    if (!selectedConv) return;
    setSending(true);
    try {
      const payload = {
        sender_id: myId,
        sender_nom: myNom,
        sender_photo_url: user?.photo_url || "",
        texte: newMessage.trim()
      };
      
      // 🔧 FIX: Ajouter le fichier attaché s'il existe
      if (attachedFile) {
        payload.fichier = attachedFile;
      }
      
      const r = await axios.post(`${API_URL}/api/messagerie/conversations/${selectedConv.id}/messages`, payload);
      if (r.status === 200) {
        setNewMessage("");
        setAttachedFile(null);
        await loadMessages(selectedConv.id);
        await loadConversations();
      }
    } catch { toast.error("Erreur envoi"); }
    finally { setSending(false); }
  };

  const getConvLabel = (conv) => {
    if (conv.is_group) return conv.group_name || "Groupe";
    const partnerId = (conv.participants || []).find(p => p !== myId);
    return (conv.participant_noms || {})[partnerId] || "Inconnu";
  };

  const getPartnerPhoto = (conv) => {
    if (conv.is_group) return null;
    const partnerId = (conv.participants || []).find(p => p !== myId);
    // ✅ Utiliser participant_photos du backend (photos des users)
    const photoFromConv = (conv.participant_photos || {})[partnerId];
    if (photoFromConv) return photoFromConv;
    // Fallback: chercher dans la liste employes (au cas où)
    const emp = employes.find(e => e.id === partnerId);
    return emp?.photo_url || null;
  };

  const getConvInitial = (conv) => {
    if (conv.is_group) return <Users className="w-4 h-4" />;
    return getConvLabel(conv)[0]?.toUpperCase() || "?";
  };

  const unreadForConv = (conv) => (conv.unread || {})[myId] || 0;

  const deleteConversation = async () => {
    if (!selectedConv) return;
    const convName = getConvLabel(selectedConv);
    if (!window.confirm(`Supprimer définitivement la conversation avec "${convName}" ?\n\nTous les messages seront perdus.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/messagerie/conversations/${selectedConv.id}`);
      toast.success("Conversation supprimée");
      setSelectedConv(null);
      setMessages([]);
      setMobileView('list');
      loadConversations();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredEmployes = employes.filter(e =>
    `${e.prenom} ${e.nom}`.toLowerCase().includes(search.toLowerCase())
  );

  const convsBadge = conversations.reduce((s, c) => s + unreadForConv(c), 0);

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <MessageSquare className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            Messagerie interne
            {convsBadge > 0 && <Badge className="bg-blue-500 text-white text-xs">{convsBadge}</Badge>}
          </h1>
          <p className="text-sm text-slate-500">Conversations directes et groupes</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4" style={{ height: "calc(100vh - 185px)" }}>
        {/* Panneau gauche — masqué sur mobile quand chat ouvert */}
        <div className={`md:col-span-4 flex flex-col gap-3 ${mobileView === 'chat' ? 'hidden md:flex' : 'flex'}`}>
          {/* Nouveau message / groupe */}
          <Card>
            <CardContent className="pt-3 pb-3 space-y-2">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
                  <Input
                    placeholder="Trouver un employé..."
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value);
                      if (e.target.value && !employesLoaded) loadEmployees(); // ✅ OPTIMISATION: Charge au besoin
                    }}
                    className="pl-8 text-sm h-8"
                  />
                </div>
                {/* 🚀 [Phase v13.47.1 — 2026-07-18] 2 boutons distincts au lieu d'un seul.
                      • Nouveau message = conversation directe 1-à-1
                      • Nouveau groupe = groupe (comportement d'origine) */}
                <Button
                  data-testid="btn-nouveau-message"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowDirectModal(true);
                    loadEmployees();
                  }}
                  className="gap-1.5 h-8 px-2.5 flex-shrink-0"
                  title="Écrire un message à une personne"
                  aria-label="Écrire un nouveau message à une personne"
                >
                  <MessageSquarePlus className="w-3.5 h-3.5" />
                </Button>
                <Button
                  data-testid="btn-nouveau-groupe"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowGroupModal(true);
                    loadEmployees(); // ✅ OPTIMISATION: Charge les employés seulement au besoin
                  }}
                  className="gap-1.5 h-8 px-2.5 flex-shrink-0"
                  title="Créer un nouveau groupe"
                  aria-label="Créer un nouveau groupe de discussion"
                >
                  <Users className="w-3.5 h-3.5" />
                </Button>
              </div>
              {search && (
                <div className="space-y-1 max-h-44 overflow-y-auto">
                  {filteredEmployes.map(e => (
                    <button
                      key={e.id}
                      onClick={() => openDirect(e)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-50 text-sm flex items-center gap-2"
                    >
                      <EmployeeAvatar photoUrl={e.photo_url} prenom={e.prenom} nom={e.nom} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{e.prenom} {e.nom}</p>
                        <p className="text-xs text-gray-400">{e.role}</p>
                      </div>
                    </button>
                  ))}
                  {filteredEmployes.length === 0 && <p className="text-xs text-gray-400 px-3">Aucun résultat</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Liste conversations */}
          <Card className="flex-1 overflow-hidden flex flex-col">
            <CardHeader className="pb-2 pt-3 flex-shrink-0">
              <CardTitle className="text-sm font-semibold text-slate-600">
                {conversations.length > 0 ? `${conversations.length} conversation${conversations.length > 1 ? "s" : ""}` : "Conversations"}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-y-auto flex-1">
              {conversations.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">Aucune conversation</p>
              ) : (
                conversations.map(conv => {
                  const unread = unreadForConv(conv);
                  const isSelected = selectedConv?.id === conv.id;
                  return (
                    <button
                      key={conv.id}
                      data-testid={`conv-${conv.id}`}
                      onClick={() => selectConv(conv)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-blue-50/70 transition-colors ${isSelected ? "bg-blue-50 border-l-[3px] border-l-blue-500" : ""}`}
                    >
                      <div className="flex items-center gap-3">
                        {conv.is_group ? (
                          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 bg-gradient-to-br from-purple-500 to-indigo-600">
                            <Users className="w-4 h-4" />
                          </div>
                        ) : (
                          <EmployeeAvatar photoUrl={getPartnerPhoto(conv)} prenom={getConvLabel(conv).split(' ')[0]} nom={getConvLabel(conv).split(' ').slice(1).join(' ')} size="sm" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <p className={`text-sm truncate ${unread > 0 ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>
                              {getConvLabel(conv)}
                            </p>
                            {unread > 0 && <Badge className="ml-1 min-w-[18px] h-4 text-[10px] bg-blue-500 px-1">{unread}</Badge>}
                          </div>
                          <p className={`text-xs truncate mt-0.5 ${unread > 0 ? "text-slate-500" : "text-gray-400"}`}>
                            {conv.last_message_preview || "Aucun message"}
                          </p>
                        </div>
                        <p className="text-[10px] text-gray-400 flex-shrink-0">{timeAgo(conv.last_message_at)}</p>
                      </div>
                    </button>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        {/* Panneau droit — masqué sur mobile quand liste affichée */}
        <div className={`md:col-span-8 flex flex-col ${mobileView === 'list' ? 'hidden md:flex' : 'flex'}`}>
          {selectedConv ? (
            <Card className="flex-1 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3 flex-shrink-0">
                <button
                  className="md:hidden mr-1 text-gray-500 hover:text-gray-700"
                  onClick={() => setMobileView('list')}
                  title="Retour aux conversations"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                {selectedConv.is_group ? (
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 bg-gradient-to-br from-purple-500 to-indigo-600">
                    <Users className="w-4 h-4" />
                  </div>
                ) : (
                  <EmployeeAvatar photoUrl={getPartnerPhoto(selectedConv)} prenom={getConvLabel(selectedConv).split(' ')[0]} nom={getConvLabel(selectedConv).split(' ').slice(1).join(' ')} size="sm" />
                )}
                <div className="flex-1">
                  <p className="font-semibold text-slate-800">{getConvLabel(selectedConv)}</p>
                  {selectedConv.is_group && (
                    <p className="text-xs text-gray-400">{selectedConv.participants?.length} membres</p>
                  )}
                </div>
                <button
                  onClick={deleteConversation}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="Supprimer la conversation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Messages - ZONE À HAUTEUR FIXE */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/50" style={{ maxHeight: "calc(100vh - 400px)" }}>
                {messages.map(msg => {
                  const isMe = msg.sender_id === myId;
                  // 🚀 [Phase v13.47 — 2026-07-18] Timestamp précis + ✓/✓✓.
                  const tsPrecis = formatTimestampFR(msg.created_at);
                  const readStatus = isMe
                    ? computeReadStatus(msg, selectedConv, selectedConv?.participant_noms || {})
                    : null;
                  return (
                    <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[72%] flex gap-2 ${isMe ? "flex-row-reverse" : ""}`}>
                        {!isMe && (
                          <div className="flex-shrink-0 mt-5">
                            <EmployeeAvatar photoUrl={msg.sender_photo_url} prenom={(msg.sender_nom || '?').split(' ')[0]} nom={(msg.sender_nom || '').split(' ').slice(1).join(' ')} size="xs" />
                          </div>
                        )}
                        <div>
                        {!isMe && selectedConv.is_group && (
                          <p className="text-xs text-gray-400 mb-1 px-1">{msg.sender_nom}</p>
                        )}
                        {!isMe && !selectedConv.is_group && (
                          <p className="text-xs text-gray-400 mb-1 px-1">{msg.sender_nom}</p>
                        )}
                        <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isMe ? "bg-blue-500 text-white rounded-br-sm" : "bg-white text-slate-800 rounded-bl-sm border border-gray-200 shadow-sm"}`}>
                          {msg.texte && <MessageTextRenderer texte={msg.texte} isMe={isMe} />}
                          <FilePreview
                            fichier={msg.fichier}
                            isMe={isMe}
                            onOpenLightbox={setLightboxFichier}
                          />
                        </div>
                        <div className={`flex items-center gap-1 mt-1 px-1 ${isMe ? "justify-end" : "justify-start"}`}>
                          <span
                            className="text-[10px] text-gray-400"
                            title={tsPrecis}
                            data-testid={`msg-timestamp-${msg.id}`}
                          >
                            {timeAgo(msg.created_at)}
                          </span>
                          {isMe && readStatus && (
                            <span
                              className={`inline-flex items-center ${readStatus.read ? 'text-blue-500' : 'text-gray-400'}`}
                              title={
                                readStatus.read
                                  ? (readStatus.readByNames.length > 0
                                      ? `Lu par : ${readStatus.readByNames.join(', ')}`
                                      : 'Lu')
                                  : 'Envoyé, non lu'
                              }
                              data-testid={`msg-read-status-${msg.id}`}
                              data-read={readStatus.read ? '1' : '0'}
                            >
                              {readStatus.read ? <CheckCheck className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                            </span>
                          )}
                        </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Prévisualisation fichier joint */}
              {attachedFile && (
                <div className="px-4 py-2 bg-blue-50 border-t border-blue-200 flex items-center gap-2">
                  {attachedFile.type?.startsWith("image/") ? (
                    <Image className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  ) : (
                    <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  )}
                  <span className="text-xs text-blue-700 font-medium truncate flex-1">{attachedFile.nom}</span>
                  <span className="text-xs text-blue-400">{(attachedFile.taille / 1024).toFixed(0)} Ko</span>
                  <button onClick={() => setAttachedFile(null)}>
                    <X className="w-4 h-4 text-blue-400 hover:text-blue-600" />
                  </button>
                </div>
              )}

              {/* Zone de saisie */}
              <div className="px-4 py-3 border-t border-gray-200 bg-white">
                <div className="flex gap-2 items-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={handleFileAttach}
                  />
                  <button
                    data-testid="btn-attach-file"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={compressing}
                    className={`w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center transition-colors flex-shrink-0 ${compressing ? 'text-blue-500 border-blue-300 animate-pulse' : 'text-gray-400 hover:text-blue-500 hover:border-blue-400'}`}
                    title={compressing ? 'Compression en cours…' : 'Joindre un fichier'}
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  {/* 🚀 [Phase v13.47.1 — 2026-07-18] Textarea auto-resize.
                        • Enter (sans Shift) : envoi
                        • Shift+Enter : nouvelle ligne
                        • Ctrl/Cmd+Enter : envoi (raccourci alternatif)
                        • white-space: pre-wrap + word-break: break-word → wrap propre */}
                  <textarea
                    ref={textareaRef}
                    data-testid="message-input"
                    placeholder="Écrire un message… (Maj+Entrée pour nouvelle ligne)"
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        // Ctrl/Cmd+Enter OU Enter simple (sans Shift) → envoi
                        if ((e.ctrlKey || e.metaKey) || !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                        // Shift+Enter seul → laisser le retour ligne natif
                      }
                    }}
                    rows={1}
                    className="flex-1 resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 leading-[1.4] break-words"
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    autoComplete="off"
                    aria-label="Zone de saisie du message"
                  />
                  <button
                    data-testid="send-message-btn"
                    onClick={sendMessage}
                    disabled={(!newMessage.trim() && !attachedFile) || sending}
                    className="w-9 h-9 rounded-xl bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center disabled:opacity-40 transition-colors flex-shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="flex-1 flex items-center justify-center bg-gray-50/50">
              <div className="text-center text-gray-400 space-y-3">
                <MessageSquare className="w-14 h-14 mx-auto text-gray-200" />
                <p className="font-medium text-slate-500">Sélectionnez une conversation</p>
                <p className="text-sm">ou recherchez un employé pour commencer</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Modal Nouveau Groupe */}
      {showGroupModal && (
        <ModalNouveauGroupe
          employes={employes}
          myId={myId}
          myNom={myNom}
          onCreated={(conv) => {
            setShowGroupModal(false);
            selectConv(conv);
            loadConversations();
          }}
          onClose={() => setShowGroupModal(false)}
        />
      )}
      {/* 🚀 [Phase v13.47 — 2026-07-18] Lightbox photos : zoom, pan, téléchargement. */}
      <MessagePhotoLightbox
        fichier={lightboxFichier}
        open={!!lightboxFichier}
        onClose={() => setLightboxFichier(null)}
      />
      {/* 🚀 [Phase v13.47.1 — 2026-07-18] Modal Nouveau message (1-à-1). */}
      {showDirectModal && (
        <ModalNouveauMessage
          employes={employes}
          myId={myId}
          myNom={myNom}
          onCreated={conv => {
            setConversations(prev => {
              if (prev.find(c => c.id === conv.id)) return prev;
              return [conv, ...prev];
            });
            setSelectedConv(conv);
            setShowDirectModal(false);
            if (window.innerWidth < 768) setMobileView('chat');
          }}
          onClose={() => setShowDirectModal(false)}
        />
      )}
    </div>
  );
}
